import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { DiscountCode } from '~app/modules/checkout/models';
import { NotificationsResponse } from '~app/modules/checkout/models/notifications.model';
import { ICart, ICartSuccess } from '~shared/modules/cart/models';
import { ICartQuantity } from '~shared/modules/cart/models/shopping-cart-quantity.model';
import { TrackingData } from '~shared/modules/shopping-cart-global/models/add-to-cart.model';
import { ShoppingCartError } from '../classes/shopping-cart-error.class';
import { DEFAULT_CONFIG } from '../constants/cart-service.constants';
import { ICartMerge, IOfferLine } from '../models';
import { ICartConfig } from '../models/cart-config.model';
import { ICartService } from '../models/cart-service.model';
import { CartConfigToken } from '../tokens/cart-config.token';
import { handleError } from '../utils/handle-error';
import { OfferHelperService } from '~shared/modules/offer-line/services/offer-helper.service';
import { ApiConfig } from '~shared/constants';
import { ActiveFeaturesConfigcatService } from '~shared/modules/active-features/services';
import { FF_ADD_TO_CART_ON_APP_CART, FF_CHECKOUT_INITIALIZE_V2 } from '../constants/feature-flag.constants';
import { ICartMini } from '../models/shopping-cart-mini.model';
import { RemoteAppCartReadService } from '~shared/modules/cart/services/remote-app-cart-read.service';
import { AccountInfoService } from '~core/modules/identity/services/account-info.service';

@Injectable()
export class RemoteCartService implements ICartService {
  config: ICartConfig;
  activeContext: string;

  constructor(
    @Inject(CartConfigToken) private readonly providedConfig: ICartConfig,
    private readonly httpClient: HttpClient,
    private readonly offerHelperService: OfferHelperService,
    private readonly ffService: ActiveFeaturesConfigcatService,
    private readonly remoteCartReadService: RemoteAppCartReadService,
    private accountInfoService: AccountInfoService
  ) {
    this.config = { ...DEFAULT_CONFIG, ...this.providedConfig };
  }

  merge(
    items: {
      offer: string;
      quantity: number;
      valueAddedServices: { offer: string; quantity: number; type: string }[];
    }[]
  ): Observable<ICartMerge | ShoppingCartError> {
    const isFFAddToCartOnAppCartEnabled$ = this.ffService.isFeatureEnabled(FF_ADD_TO_CART_ON_APP_CART);

    return isFFAddToCartOnAppCartEnabled$.pipe(
      map((isEnabled) => {
        if (isEnabled) {
          const url = `${ApiConfig.SVC_BUYER_GATEWAY_URL_API_ENDPOINT()}/api/account/proxy/cart/v1/cart/all`;

          const mappedItems = items.map((item) => ({
            quantity: item?.quantity,
            offer: item?.offer,
            services: item.valueAddedServices.map(({ offer, type }) => ({
              offerId: offer,
              type,
            })),
          }));

          return { url, payload: { items: mappedItems } };
        } else {
          const url = `${this.config.cartApiUrl}/cart/all`;
          return { url, payload: { items } };
        }
      }),
      switchMap(({ url, payload }) =>
        this.accountInfoService.getAccountInfo().pipe(
          map((accountInfo) => ({
            url,
            payload,
            activeContext: accountInfo.activeContext,
          }))
        )
      ),
      switchMap(({ url, payload, activeContext }) => {
        const httpOptions = {
          headers: new HttpHeaders({
            'X-Active-Context': activeContext,
          }),
        };

        return this.httpClient.post(url, payload, httpOptions).pipe(
          map((response: ICartMerge) => response),
          catchError(handleError)
        );
      })
    );
  }

  read(): Observable<ICart | ShoppingCartError> {
    return this.remoteCartReadService.read();
  }

  readWithDiscount(discountCode: string): Observable<DiscountCode | ShoppingCartError> {
    return this.remoteCartReadService.readWithDiscount(discountCode);
  }

  addProduct(
    offer: string,
    quantity: number,
    valueAddedServices: { offer: string; quantity: number }[],
    trackingData: TrackingData
  ): Observable<IOfferLine | ShoppingCartError> {
    const isFFAddToCartOnAppCartEnabled$ = this.ffService.isFeatureEnabled(FF_ADD_TO_CART_ON_APP_CART);

    return combineLatest([this.accountInfoService.getAccountInfo(), isFFAddToCartOnAppCartEnabled$]).pipe(
      switchMap(([accountInfo, isEnabled]) => {
        const url = isEnabled
          ? `${ApiConfig.SVC_BUYER_GATEWAY_URL_API_ENDPOINT()}/api/account/proxy/cart/v1/cart`
          : `${this.config.cartApiUrl}/cart`;

        const httpOptions = {
          headers: new HttpHeaders({
            'X-Active-Context': accountInfo.activeContext,
          }),
        };

        return this.httpClient.post(url, { offer, quantity, valueAddedServices, trackingData }, httpOptions).pipe(
          map((response) => response as IOfferLine),
          catchError(handleError)
        );
      })
    );
  }

  updateQuantity(
    offer: string,
    quantity: number,
    trackingData: TrackingData
  ): Observable<IOfferLine | ShoppingCartError> {
    const url = `${this.config.cartApiUrl}/cart/${offer}`;
    return this.httpClient.patch(url, { quantity, trackingData }).pipe(
      map((response) => response as IOfferLine),
      catchError(handleError)
    );
  }

  deleteProduct(offer: string, cartLineId: string): Observable<ICartSuccess | ShoppingCartError> {
    const isFFAddToCartOnAppCartEnabled$ = this.ffService.isFeatureEnabled(FF_ADD_TO_CART_ON_APP_CART);
    return isFFAddToCartOnAppCartEnabled$.pipe(
      switchMap((isEnabled) => {
        const appCartBaseUrl = ApiConfig.SVC_BUYER_GATEWAY_URL_API_ENDPOINT();
        const appCartUrl = `${appCartBaseUrl}/api/account/proxy/cart/v1/cart/${cartLineId}`;
        const appCheckoutUrl = `${this.config.cartApiUrl}/cart/${offer}`;

        const url = isEnabled ? appCartUrl : appCheckoutUrl;
        return this.httpClient.delete(url).pipe(
          map(() => new ICartSuccess()),
          catchError(handleError)
        );
      })
    );
  }

  deleteValueAddedService(vasId: string, offerId: string): Observable<ICartSuccess | ShoppingCartError> {
    const url = `${this.config.cartApiUrl}/cart/${offerId}/vas/${vasId}`;
    return this.httpClient.delete(url).pipe(
      map(() => new ICartSuccess()),
      catchError(handleError)
    );
  }

  checkout(force = false): Observable<ICart | ICartMini | ShoppingCartError> {
    return this.getCheckoutRequest(force).pipe(
      catchError(handleError),
      catchError((error) => this.offerHelperService.handleInvalidOffersError(error))
    );
  }

  private getCheckoutRequest(force: boolean) {
    const isEnabledCheckoutV2$ = this.ffService.isFeatureEnabled(FF_CHECKOUT_INITIALIZE_V2);

    return isEnabledCheckoutV2$.pipe(
      map((isEnabled) => {
        if (isEnabled) {
          const host = ApiConfig.SVC_BUYER_GATEWAY_URL_API_ENDPOINT();
          return `${host}/api/account/proxy/checkout_v2/checkout/initialize${force ? '?force=true' : ''}`;
        }

        return `${this.config.cartApiUrl}/checkout${force ? '/force' : ''}`;
      }),
      withLatestFrom(isEnabledCheckoutV2$),
      switchMap(([url, isEnabled]) => {
        if (isEnabled) {
          return this.httpClient.post<ICartMini>(url, {});
        }

        return this.httpClient
          .get<ICart>(url)
          .pipe(tap((cart) => this.offerHelperService.handleInvalidCartOffers(cart)));
      })
    );
  }

  getItemsInCartCount(): Observable<number | null> {
    const url = `${this.config.cartApiUrl}/cart/quantity`;
    return this.httpClient.get<ICartQuantity>(url).pipe(
      map((response) => response && response.quantity),
      catchError(() => of(null))
    );
  }

  updateCart(discountCode: string | null = null): Observable<DiscountCode | ShoppingCartError> {
    return this.remoteCartReadService.readCartReview(discountCode);
  }

  getNotifications(): Observable<NotificationsResponse> {
    const url = `${ApiConfig.SVC_BUYER_GATEWAY_URL_API_ENDPOINT()}/api/account/proxy/cart/v1/cart/notifications`;
    return this.httpClient.get<NotificationsResponse>(url);
  }
}
